import * as Xstate from "@xstate/react";
import React from "react";

import { getCurrency } from "../../../app/commonOps/CommonNumberOps";
import { TW_SIZES } from "../../../app/constants/CssConstants";
import { GlobalContext } from "../../../app/stateMachines/GlobalContext";
import { AlertNotice } from "../../../components/alerts/AlertNotice";
import { FormSection } from "../../../support/FormSection";

const getMatchClass = (match: boolean) =>
  match ? TW_SIZES.colors.textSuccess : TW_SIZES.colors.textError;

const CX = {
  currencyLabel: "text-lg font-normal capitalize",
  currencyValue: "text-right text-lg font-semibold",
};

type CurrencyValueColumnProps = {
  value: number;
  classNames?: string;
};

const CurrencyValueColumnView: React.FC<CurrencyValueColumnProps> = (props) => {
  const { value, classNames = "" } = props;

  return (
    <div className={"flex items-center justify-end space-x-2"}>
      <div className={`${CX.currencyValue} ${classNames}`}>
        {getCurrency(value)}
      </div>
    </div>
  );
};

const ExtractedTotalsEditInfoPanelView: React.FC<unknown> = () => {
  // xstate
  const { bankDocDetailsService } = React.useContext(GlobalContext);
  const [bankDocDetailsState] = Xstate.useActor(bankDocDetailsService);
  const { matcher, extractedTotalsData, isCreditCard } =
    bankDocDetailsState.context;

  if (!extractedTotalsData) return null;
  const { credits, debits } = extractedTotalsData;

  const creditsLabel = isCreditCard ? "charges" : "payments";
  const debitsLabel = isCreditCard ? "payments" : "deposits";
  return (
    <>
      <div className={"grid grid-cols-2 xl:grid-cols-2"}>
        <div className={CX.currencyLabel}>{debitsLabel}</div>
        <CurrencyValueColumnView
          classNames={getMatchClass(matcher.endingEqual)}
          value={debits}
        />
      </div>
      <div className={"grid grid-cols-2 xl:grid-cols-2"}>
        <div className={CX.currencyLabel}>{creditsLabel}</div>
        <CurrencyValueColumnView
          classNames={getMatchClass(matcher.endingEqual)}
          value={credits}
        />
      </div>
    </>
  );
};

const PdfBalancesEditInfoPanelView: React.FC<unknown> = () => {
  // xstate
  const { bankDocDetailsService } = React.useContext(GlobalContext);
  const [bankDocDetailsState] = Xstate.useActor(bankDocDetailsService);
  const { pdfTotalsData } = bankDocDetailsState.context;
  const { starting, ending } = pdfTotalsData;

  return (
    <div className={"grid grid-flow-col grid-rows-2 gap-4"}>
      <div className={"col-span-4 grid grid-cols-4"}>
        <div className={"col-span-2"}>
          <div className={CX.currencyLabel}>Starting Balance</div>
        </div>
        <div className={"col-span-2"}>
          <CurrencyValueColumnView value={starting} />
        </div>
      </div>
      <div className={"col-span-4 row-span-1 grid grid-cols-4"}>
        <div className={"col-span-2"}>
          <div className={CX.currencyLabel}>Ending Balance</div>
        </div>
        <div className={"col-span-2"}>
          <CurrencyValueColumnView value={ending} />
        </div>
      </div>
    </div>
  );
};

const DifferenceEditInfoPanelView: React.FC<unknown> = () => {
  // xstate
  const { bankDocDetailsService } = React.useContext(GlobalContext);
  const [bankDocDetailsState] = Xstate.useActor(bankDocDetailsService);
  const { difference, matcher } = bankDocDetailsState.context;

  return (
    <>
      <div className={"grid grid-cols-2"}>
        <div className={CX.currencyLabel}>Cleared balance</div>
        <CurrencyValueColumnView
          classNames={getMatchClass(matcher.endingEqual)}
          value={difference.calcEnding}
        />
      </div>

      {matcher.endingEqual ? (
        <div className={"grid-col-1 flex grid items-center justify-center"}>
          <AlertNotice label={"matched!"} />
        </div>
      ) : (
        <div className={"grid grid-cols-2"}>
          <div className={CX.currencyLabel}>Difference</div>
          <CurrencyValueColumnView
            classNames={getMatchClass(matcher.endingEqual)}
            value={difference.endingDiff}
          />
        </div>
      )}
    </>
  );
};

export const BankDocOutputReadOnlyInfoPanelsView: React.FC<unknown> = ({
  children,
}) => {
  // xstate
  const { bankDocDetailsService } = React.useContext(GlobalContext);
  const [state] = Xstate.useActor(bankDocDetailsService);
  const { pdfTotalsData } = state.context;

  if (!pdfTotalsData) return null;

  const panelContainerBaseCx =
    "col-span-1 px-4 border-b-2 xl:border-b-0 xl:border-r-2";
  const panelContainerEndCx = `${panelContainerBaseCx} border-b-0 xl:border-r-0`;
  const panelBaseCx = "mx-auto grid grid-cols-1 gap-4 xl:max-w-md";

  return (
    <div className={"z-30 grid grid-cols-1 border-b-2 xl:sticky xl:top-0.5"}>
      <FormSection
        name={"Summary"}
        viewFormat={"compact"}
        showHeading={false}
        extraCx={"overflow-visible"}>
        <div className={"grid grid-cols-1 gap-4 xl:grid-cols-4"}>
          <div className={panelContainerBaseCx}>
            <div className={panelBaseCx}>
              <PdfBalancesEditInfoPanelView />
            </div>
          </div>

          <div className={panelContainerBaseCx}>
            <div className={panelBaseCx}>
              <ExtractedTotalsEditInfoPanelView />
            </div>
          </div>

          <div className={panelContainerBaseCx}>
            <div className={panelBaseCx}>
              <DifferenceEditInfoPanelView />
            </div>
          </div>

          <div className={`${panelContainerEndCx}`}>
            <div className={`${panelBaseCx} xl:max-w-xl`}>{children}</div>
          </div>
        </div>
      </FormSection>
    </div>
  );
};
